import { Client } from '@/api/main/client'
import { SnapshotFrom } from 'xstate'
import { NavigationState, RouteModuleState } from '../types'
import { ReactNode } from 'react'
import MaintenanceModeAlert from './MaintenanceModeAlert'
import SyncInProgressAlert from './SyncInProgressAlert'
import SyncFinishedAlert from './SyncFinishedAlert'
import FetchingDriversAlert from './FetchingDriversAlert'
import FetchingDrivesAlert from './FetchingDrivesAlert'
import InitializingAlert from './InitializingAlert'
import NoScheduledDriveAlert from './NoScheduledDriveAlert'
import { CarServerUnreachableAlert } from './CarServerUnreachableAlert'
import NextRouteAlert from './NextRouteAlert'
import PauseAndGoToDropoffAlert from './PauseAndGoToDropoffAlert'
import GoToGarageAlert from './GoToGarageAlert'
import TimeToDropoffAlert from './TimeToDropoffAlert'
import PatrolFinishedAlert from './PatrolFinishedAlert'
import { head } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { NavigationDataResponse } from '@/api/navigationApi/types.gen'
import DriverSelection from './DriverSelection'
import StackedAlertsContainer from './StackedAlertsContainer'
import { NavigationMode } from '../useDirectionsData'
import { navigationMachine } from '../navigationMachine'
import CheckingSyncAlert from './CheckingSyncAlert'
import { useCurrentTime } from '@/stores/currentTime'
import { shouldShowGoToGarageInNoScheduledDrive } from '../dropoffAlertRules'
import LowGPSAccuracyAlert from './LowGPSAccuracyAlert'
import AlertContainer from './AlertContainer'

const getGarageAlert = (
  navigationMode: NavigationMode,
  routeModuleState: NavigationDataResponse['route_module_state'] | undefined,
  shiftEndingStartDate: Date | undefined,
  dropoffTime: string | undefined,
  now: Date,
  dropoffDriveDurationSeconds: number | undefined
): ReactNode => {
  const isDriving = routeModuleState === 'Driving'

  if (routeModuleState === 'NoScheduledDrive') {
    return shouldShowGoToGarageInNoScheduledDrive(dropoffTime, now, dropoffDriveDurationSeconds) ? (
      <GoToGarageAlert timeToDropoff={{ seconds: 0 }} />
    ) : null
  }

  if (navigationMode.type === 'MANUAL_GARAGE') {
    return isDriving ? (
      <PauseAndGoToDropoffAlert timeToDropoff={{ seconds: 0 }} />
    ) : (
      <GoToGarageAlert timeToDropoff={{ seconds: 0 }} />
    )
  }

  if (navigationMode.type === 'AUTOMATIC_GARAGE') {
    if (navigationMode.timeRemainingSeconds <= 0) {
      return isDriving ? (
        <PauseAndGoToDropoffAlert timeToDropoff={{ seconds: 0 }} />
      ) : (
        <GoToGarageAlert timeToDropoff={{ seconds: 0 }} />
      )
    }
    return (
      <TimeToDropoffAlert
        dropoffStartDate={shiftEndingStartDate}
        driveDuration={navigationMode.driveDurationSeconds}
        scheduledEnd={navigationMode.scheduledEndTime}
      />
    )
  }

  return null
}

const AlertRenderer = ({
  state,
  client,
  navigationData,
  onStart,
  onFinish,
  isFinishing,
  navigationMode,
  shiftEndingStartDate,
  isGPSAccuracyLow,
  dropoffDriveDurationSeconds,
}: {
  state: SnapshotFrom<typeof navigationMachine>
  client: Client | undefined
  navigationData: NavigationDataResponse | undefined
  onStart: () => void
  onFinish: () => void
  isFinishing: boolean
  navigationMode: NavigationMode
  shiftEndingStartDate: Date | undefined
  isGPSAccuracyLow: boolean
  dropoffDriveDurationSeconds: number | undefined
}) => {
  const { t } = useTranslation(['alerts', 'navigation'])
  const now = useCurrentTime(1000)
  const routeModuleState = navigationData?.route_module_state
  const isPatrolProgressVisible =
    routeModuleState === 'Driving' && navigationData?.drive_data && navigationData?.is_freeride !== true
  const baseOffset = isPatrolProgressVisible ? 80 : 32

  const garageAlert = getGarageAlert(
    navigationMode,
    routeModuleState,
    shiftEndingStartDate,
    navigationData?.scheduled_shift?.dropoff_time,
    now,
    dropoffDriveDurationSeconds
  )

  const lowGPSAccuracyAlert = isGPSAccuracyLow ? <LowGPSAccuracyAlert /> : null

  const isMonitoring = (routeState: RouteModuleState) => state.matches({ [NavigationState.Monitoring]: routeState })

  if (state.matches(NavigationState.CheckingSync)) return <CheckingSyncAlert />

  if (state.matches(NavigationState.SyncInProgress)) return <SyncInProgressAlert />

  if (state.matches(NavigationState.SyncFinished)) return <SyncFinishedAlert />

  if (isMonitoring(RouteModuleState.NoDriverSelected)) return <DriverSelection client={client} />

  if (isMonitoring(RouteModuleState.FetchingDrivers)) return <FetchingDriversAlert />

  if (isMonitoring(RouteModuleState.Fetching)) return <FetchingDrivesAlert />

  if (isMonitoring(RouteModuleState.Initializing)) return <InitializingAlert />

  if (isMonitoring(RouteModuleState.CommunicationError)) return <CarServerUnreachableAlert />

  if (isMonitoring(RouteModuleState.Maintenance))
    return (
      <StackedAlertsContainer baseOffset={baseOffset}>
        <MaintenanceModeAlert />
        {garageAlert}
        {lowGPSAccuracyAlert}
      </StackedAlertsContainer>
    )

  if (isMonitoring(RouteModuleState.NoScheduledDrive)) {
    return (
      <StackedAlertsContainer baseOffset={baseOffset}>
        <NoScheduledDriveAlert />
        {garageAlert}
        {lowGPSAccuracyAlert}
      </StackedAlertsContainer>
    )
  }

  if (isMonitoring(RouteModuleState.Starting))
    return (
      <StackedAlertsContainer baseOffset={baseOffset}>
        <AlertContainer
          type="alert"
          alertText={t('driveStarting')}
          alertColor="teal"
          position="bottom"
          noOverlay
          height="tiny"
        />
      </StackedAlertsContainer>
    )

  if (isMonitoring(RouteModuleState.Pausing))
    return (
      <StackedAlertsContainer baseOffset={baseOffset}>
        <AlertContainer
          type="alert"
          alertText={t('drivePausing')}
          alertColor="blue"
          position="bottom"
          noOverlay
          height="tiny"
        />
      </StackedAlertsContainer>
    )

  if (isMonitoring(RouteModuleState.Resuming))
    return (
      <StackedAlertsContainer baseOffset={baseOffset}>
        <AlertContainer
          type="alert"
          alertText={t('driveResuming')}
          alertColor="teal"
          position="bottom"
          noOverlay
          height="tiny"
        />
      </StackedAlertsContainer>
    )

  if (isMonitoring(RouteModuleState.Skipping))
    return (
      <StackedAlertsContainer baseOffset={baseOffset}>
        <AlertContainer
          type="alert"
          alertText={t('segmentSkipping')}
          alertColor="orange"
          position="bottom"
          noOverlay
          height="tiny"
        />
      </StackedAlertsContainer>
    )

  if (isMonitoring(RouteModuleState.Finishing))
    return (
      <StackedAlertsContainer baseOffset={baseOffset}>
        <AlertContainer
          type="alert"
          alertText={t('driveFinishing')}
          alertColor="orange"
          position="bottom"
          noOverlay
          height="tiny"
        />
      </StackedAlertsContainer>
    )

  if (
    (isMonitoring(RouteModuleState.Fetched) || isMonitoring(RouteModuleState.Paused)) &&
    !isMonitoring(RouteModuleState.Freeride)
  ) {
    if (navigationMode.type === 'MANUAL_GARAGE')
      return <StackedAlertsContainer baseOffset={baseOffset}>{garageAlert}</StackedAlertsContainer>

    const hasNextRouteData =
      navigationData?.driver !== undefined && navigationData?.drive_data?.start_after !== undefined

    const shouldShowNextRoute = navigationMode.type === 'NEXT_ROUTE' && hasNextRouteData

    const shouldShowNextRouteAlongsideGarage = navigationMode.type === 'AUTOMATIC_GARAGE' && hasNextRouteData

    const streetName =
      shouldShowNextRoute || shouldShowNextRouteAlongsideGarage
        ? (head(
            navigationData?.future_street_segments?.filter(segment => {
              return !segment.street_name.startsWith('NN')
            })
          )?.street_name ??
          t('unknownStreetName', { ns: 'navigation', defaultValue: 'Unknown street name' }).toString())
        : undefined

    const nextRouteAlert =
      shouldShowNextRoute || shouldShowNextRouteAlongsideGarage ? (
        <NextRouteAlert
          routeId={navigationData?.drive_data?.route_id ?? ''}
          startAfter={
            navigationData?.drive_data?.start_after ? new Date(navigationData.drive_data.start_after) : new Date()
          }
          streetName={streetName!}
          onStart={onStart}
        />
      ) : null

    if (shouldShowNextRouteAlongsideGarage)
      return (
        <StackedAlertsContainer baseOffset={baseOffset}>
          {garageAlert}
          {nextRouteAlert}
          {lowGPSAccuracyAlert}
        </StackedAlertsContainer>
      )

    if (navigationMode.type === 'AUTOMATIC_GARAGE')
      return (
        <StackedAlertsContainer baseOffset={baseOffset}>
          {garageAlert}
          {lowGPSAccuracyAlert}
        </StackedAlertsContainer>
      )

    if (shouldShowNextRoute)
      return (
        <StackedAlertsContainer baseOffset={baseOffset}>
          {nextRouteAlert}
          {lowGPSAccuracyAlert}
        </StackedAlertsContainer>
      )

    return null
  }

  if (isMonitoring(RouteModuleState.Driving))
    return (
      <StackedAlertsContainer baseOffset={baseOffset}>
        {garageAlert}
        {lowGPSAccuracyAlert}
      </StackedAlertsContainer>
    )

  if (isMonitoring(RouteModuleState.Finished))
    return <PatrolFinishedAlert client={client} onFinish={onFinish} isFinishing={isFinishing} />

  return null
}

export default AlertRenderer
